import { Injectable, OnDestroy } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { MatSnackBar, MatSnackBarRef } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { MessageLogDTO } from "src/app/data/schema/Communication/MessageLogDTO";
import { SiglaTemplateDTO } from "src/app/data/schema/Communication/SiglaTemplateDTO";
import { TemplateBodyTypeDTO } from "src/app/data/schema/Communication/TemplateBodyTypeDTO";
import { TemplateMessageTypeDTO } from "src/app/data/schema/Communication/TemplateMessageTypeDTO";
import { UserCertificateQueryParamsDTO } from "src/app/data/schema/UserCourse/userCertificate/UserCertificateQueryParamsDTO";
import { MessageLogService } from "src/app/data/service/Communication/message-log.service";
import { ApiExamsReportService } from "src/app/data/service/exam/api-exams-report.service";
import { CertificateViewerComponent } from "src/app/shared/component/certificate-viewer/certificate-viewer.component";
import { GenericNotificationComponent } from "src/app/shared/component/generic-notification/generic-notification.component";
import { AuthService } from "../auth.service";
import { AchievementMessageService } from "../gamification/achievement-message.service";
import { SubscriptionService } from "../subscription.service";
import {
    CommunicationMessageComponent,
    notificationData
} from "./components/communication-message/communication-message.component";
import enUS from "./i18n/en-US.json";
import esES from "./i18n/es-ES.json";
import ptBR from "./i18n/pt-BR.json";

@Injectable({
    providedIn: "root"
})
export class CommunicationService implements OnDestroy {
    private interval: NodeJS.Timeout;

    msgsToShow: MessageLogDTO[] = [];
    msgOpened: MessageLogDTO | null;
    notificationTimeInMinutes = 5;
    getMessagesAgainInSec = 5;
    adminMsgsStorageId = "Admin_Notifications_Ids";
    private _unsubscribeAll: Subject<any>;

    private updateCourseProgression$ = new BehaviorSubject<string>(null);
    public getUpdateCourseProgression = (): Observable<string> => this.updateCourseProgression$.asObservable();

    constructor(
        private messageLogService: MessageLogService,
        private achievementMsgService: AchievementMessageService,
        private authService: AuthService,
        private snackBar: MatSnackBar,
        private translate: TranslateService,
        private router: Router,
        private subscriptionService: SubscriptionService,
        private examsReportService: ApiExamsReportService,
        private dialog: MatDialog
    ) {
        this.translate.setTranslation("pt-BR", ptBR, true);
        this.translate.setTranslation("en-US", enUS, true);
        this.translate.setTranslation("es-ES", esES, true);

        this._unsubscribeAll = new Subject();
    }
    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    startVerify(): void {
        clearInterval(this.interval);
        this.interval = setInterval(() => {
            const userId = this.authService.getClaimsUserId();
            if (!userId) {
                clearInterval(this.interval);
                return;
            }

            this.getMessages();
            this.showMessages();
        }, 1000 * this.getMessagesAgainInSec);
    }

    getMessages(): void {
        if (this.msgOpened) return;

        this.messageLogService
            .list({
                onlyNotSent: true,
                userId: this.authService.getClaimsUserId(),
                orderBy: "createdat",
                find: "",
                skip: 0,
                take: 100,
                sortOrder: "asc",
                types: [TemplateMessageTypeDTO.UiPush, TemplateMessageTypeDTO.AdminPush]
            })
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((res) => {
                if (!res.data) return;
                this.setAdminMessages(res.data);
                this.msgsToShow = res.data?.filter((m) => m.tipo == TemplateMessageTypeDTO.UiPush);
                this.showMessages();
            });
    }

    setAdminMessages(msgs: MessageLogDTO[]): void {
        msgs = msgs?.filter((m) => m.tipo == TemplateMessageTypeDTO.AdminPush) ?? [];
        if (!msgs || msgs.length == 0) return;

        let hasNew = false;
        const storaged = JSON.parse(localStorage.getItem(this.adminMsgsStorageId)) ?? ([] as MessageLogDTO[]);
        msgs.forEach((msg) => {
            if (storaged?.find((m) => m.id == msg.id)) return;
            hasNew = true;
        });

        localStorage.setItem(this.adminMsgsStorageId, JSON.stringify(msgs));

        if (hasNew) {
            this.msgsToShow.push({
                id: "admin-notifications",
                criadoEm: null,
                destinatario: null,
                enviadoEm: null,
                mensagem: "",
                parameters: null,
                subscriptionId: null,
                template: SiglaTemplateDTO.AdminNotifications,
                tipo: null,
                titulo: null,
                userId: null
            });
            this.showMessages();
        }
    }

    showAdminsNotifications(msgId: string): void {
        const snack = this.openNotfication(
            this.translate.instant("NOTIFICATION.ADMIN_HAS_NOTIFICATIONS"),
            this.translate.instant("NOTIFICATION.GO_TO_ADMIN")
        );

        snack
            .onAction()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                window.open(
                    "about:blank",
                    "_blank"
                ).location.href = `https://${this.subscriptionService.currentSubscription.host}/admin2/home`;
            });

        snack
            .afterDismissed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.removeMsg(msgId);
                this.msgOpened = null;
            });
    }

    showMessages(): void {
        if (!this.msgsToShow || this.msgsToShow.length == 0 || this.msgOpened) return;

        const msg = this.msgsToShow[0];
        this.msgOpened = msg;
        switch (msg.template) {
            case SiglaTemplateDTO.Achievement: {
                this.achievementMsgService
                    .showAchievement(msg.parameters["achievementDetailId"])
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => {
                        this.markMessageAsSent(msg.id);
                    });

                break;
            }
            case SiglaTemplateDTO.COMPLETION_CERTIFICATE: {
                const txt = this.translate.instant("NOTIFICATION.CERTIFICATE_ISSUED.MSG", {
                    course: msg.parameters["course_title"]
                });

                const snack = this.openNotfication(txt, this.translate.instant("NOTIFICATION.CERTIFICATE_ISSUED.VIEW"));
                snack
                    .afterDismissed()
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => this.markMessageAsSent(msg.id));
                snack
                    .onAction()
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => {
                        const cfg: MatDialogConfig<UserCertificateQueryParamsDTO> = {
                            data: {
                                skip: 0,
                                take: 1,
                                subscriptionId: this.subscriptionService.currentSubscription?.id,
                                id: msg.parameters["userCertificateId"],
                                userId: this.authService.getClaimsUserId(),
                                fillCourse: true
                            },
                            maxHeight: "99dvh",
                            maxWidth: "99vw"
                        };
                        this.dialog.open(CertificateViewerComponent, cfg);
                    });
                break;
            }
            case SiglaTemplateDTO.ExamsReportCompleted: {
                const snack = this.openNotfication(
                    this.translate.instant("NOTIFICATION.EXAMS_REPORT_COMPLETED.MSG"),
                    this.translate.instant("NOTIFICATION.EXAMS_REPORT_COMPLETED.DOWNLOAD")
                );

                snack
                    .afterDismissed()
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => this.markMessageAsSent(msg.id));
                snack
                    .onAction()
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => {
                        this.examsReportService
                            .GetRequestById(msg.parameters["RequestId"])
                            .pipe(takeUntil(this._unsubscribeAll))
                            .subscribe((request) => {
                                window.open("about:blank", "_blank").location.href = request.reportUrl;
                            });
                    });
                break;
            }
            case SiglaTemplateDTO.EXAM_CORRECTED: {
                const txt = this.translate.instant("NOTIFICATION.EXAM_CORRECTED.MSG", {
                    exam: msg.parameters["Exam"],
                    course: msg.parameters["Course"]
                });

                const snack = this.openNotfication(txt, this.translate.instant("NOTIFICATION.EXAM_CORRECTED.RESULTS"));
                snack
                    .afterDismissed()
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => this.markMessageAsSent(msg.id));
                snack
                    .onAction()
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => {
                        this.router.navigate(["/exam", msg.parameters["ExamId"], "preview"]);
                    });
                break;
            }
            case SiglaTemplateDTO.EXAM_WAITING_MARK: {
                const txt = this.translate.instant("NOTIFICATION.EXAM_WAITING_MARK.MSG", {
                    course: msg.parameters["Course"]
                });

                const snack = this.openNotfication(txt, this.translate.instant("NOTIFICATION.EXAM_WAITING_MARK.MARK"));
                snack
                    .afterDismissed()
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => this.markMessageAsSent(msg.id));
                snack
                    .onAction()
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => {
                        this.router.navigate([
                            "/exam",
                            msg.parameters["ExamId"],
                            "trials",
                            msg.parameters["TrialId"],
                            "mark"
                        ]);
                    });
                break;
            }
            case SiglaTemplateDTO.UserCourseProgressionUpdated: {
                this.updateCourseProgression$.next(msg.parameters["courseId"]);
                const txt = this.translate.instant("NOTIFICATION.COURSE_PROGRESSION_RESETED.MSG", {
                    course: msg.parameters["course"]
                });

                const snack = this.openNotfication(txt);
                snack
                    .afterDismissed()
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => this.markMessageAsSent(msg.id));
                break;
            }
            case SiglaTemplateDTO.ExamWaitingSentAutoMarking: {
                const txt = this.translate.instant("NOTIFICATION.EXAM_WAITING_SENT_AUTO_MARK.MSG", {
                    course: msg.parameters["Course"]
                });
                const snack = this.openNotfication(
                    txt,
                    this.translate.instant("NOTIFICATION.EXAM_WAITING_SENT_AUTO_MARK.EXAMS")
                );
                snack
                    .afterDismissed()
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => this.markMessageAsSent(msg.id));
                snack
                    .onAction()
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => {
                        localStorage.setItem("teach_selectedTab", "3");
                        localStorage.setItem(
                            "user-exams-filters",
                            JSON.stringify({
                                blockedStudents: null,
                                initialDate: null,
                                finalDate: null,
                                status: ["InMarking", "WaitingMarking"]
                            })
                        );
                        this.router.navigate(["home", "ensinar"]);
                    });

                break;
            }
            case SiglaTemplateDTO.AdminNotifications: {
                this.showAdminsNotifications(msg.id);
                break;
            }
            case SiglaTemplateDTO.UserSaved: {
                this.authService
                    .applyRefreshToken(msg.subscriptionId, true)
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => {
                        this.markMessageAsSent(msg.id);
                    });
                break;
            }
            case SiglaTemplateDTO.ConfirmacaoDeInscricaoEmCurso: {
                if (msg.parameters["OriginType"] === "Himself") {
                    this.markMessageAsSent(msg.id);
                    break;
                }

                const msgTxt = this.translate.instant("NOTIFICATION.REGISTRATION_CONFIRMED.MSG_COURSE", {
                    nome_curso: msg.parameters["nome_curso"]
                });
                const action = this.translate.instant("NOTIFICATION.REGISTRATION_CONFIRMED.ACTION");
                const snack = this.openNotfication(msgTxt, action);

                snack
                    .afterDismissed()
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => this.markMessageAsSent(msg.id));

                snack
                    .onAction()
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => {
                        this.router.navigate(["home", "cursos"]);
                        this.markMessageAsSent(msg.id);
                    });
                break;
            }
            case SiglaTemplateDTO.ConfirmacaoDeInscricaoEmTrilha: {
                if (msg.parameters["OriginType"] === "Himself") {
                    this.markMessageAsSent(msg.id);
                    break;
                }

                const msgTxt = this.translate.instant("NOTIFICATION.REGISTRATION_CONFIRMED.MSG_LEARNINGPATH", {
                    nome_trilha: msg.parameters["nome_trilha"]
                });
                const action = this.translate.instant("NOTIFICATION.REGISTRATION_CONFIRMED.ACTION");
                const snack = this.openNotfication(msgTxt, action);

                snack
                    .afterDismissed()
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => this.markMessageAsSent(msg.id));

                snack
                    .onAction()
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => {
                        this.router.navigate(["cursos", msg.parameters["CourseId"], "presentation"]);
                        this.markMessageAsSent(msg.id);
                    });
                break;
            }
            case SiglaTemplateDTO.ConfirmacaoDeInscricaoEmPacote: {
                if (msg.parameters["OriginType"] === "Himself") {
                    this.markMessageAsSent(msg.id);
                    break;
                }

                const msgTxt = this.translate.instant("NOTIFICATION.REGISTRATION_CONFIRMED.MSG_PACKAGE", {
                    nome_pacote: msg.parameters["nome_pacote"]
                });
                const action = this.translate.instant("NOTIFICATION.REGISTRATION_CONFIRMED.ACTION");
                const snack = this.openNotfication(msgTxt, action);

                snack
                    .afterDismissed()
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => this.markMessageAsSent(msg.id));

                snack
                    .onAction()
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => {
                        this.router.navigate(["home", "cursos"]);
                        this.markMessageAsSent(msg.id);
                    });
                break;
            }

            default: {
                this.getMessageBody(msg.id);
                break;
            }
        }
    }

    getMessageBody(msgId: string): void {
        this.messageLogService.getMessageBody(msgId, this.translate.currentLang).subscribe((template) => {
            switch (template.templateBodyType) {
                case TemplateBodyTypeDTO.Text: {
                    const snack = this.openNotfication(template.templateBodyMessage);
                    snack
                        .afterDismissed()
                        .pipe(takeUntil(this._unsubscribeAll))
                        .subscribe(() => {
                            this.markMessageAsSent(msgId);
                        });
                    break;
                }
                case TemplateBodyTypeDTO.Html: {
                    const ref = this.dialog.open(GenericNotificationComponent, {
                        data: template.templateBodyMessage,
                        width: "1200px",
                        closeOnNavigation: false,
                        disableClose: true
                    });
                    ref.afterClosed()
                        .pipe(takeUntil(this._unsubscribeAll))
                        .subscribe(() => {
                            this.markMessageAsSent(msgId);
                        });
                    break;
                }
                default:
                    throw new Error(`template body type '${template.templateBodyType}' not supported`);
            }
        });
    }

    removeMsg(id: string): void {
        const i = this.msgsToShow.findIndex((m) => m.id == id);
        if (i < 0) return;

        this.msgsToShow.splice(i, 1);
        if (this.msgsToShow?.length > 0) {
            setTimeout(() => this.showMessages(), 500);
        }
    }

    markMessageAsSent(msgId: string): void {
        this.removeMsg(msgId);
        this.msgOpened = null;

        this.messageLogService.markMessageAsSent(msgId).pipe(takeUntil(this._unsubscribeAll)).subscribe();
    }

    openNotfication(msgTxt: string, action: string | null = null): MatSnackBarRef<CommunicationMessageComponent> {
        return this.snackBar.openFromComponent<CommunicationMessageComponent>(CommunicationMessageComponent, {
            data: { message: msgTxt, action: action } as notificationData,
            horizontalPosition: "center",
            verticalPosition: "top",
            duration: 1000 * 60 * this.notificationTimeInMinutes
        });
    }
}
